import React, { useState, useEffect, useRef } from "react";
import { Timestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import SignaturePad from "react-signature-canvas";
import { X } from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const FormRenderer = ({
  form,
  onSubmit,
  onClose,
  // If isModal = true => we assume direct URL usage and show an overlay
  // If isModal = false => we assume embedded usage with no overlay/padding
  isModal = true,
  // Called by parent if we want auto-resizing in iframe
  onHeightChange = null,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const signaturePadRefs = useRef({});
  const containerRef = useRef(null);

  // Initialize formData once on mount
  useEffect(() => {
    const initialData = {};
    form.elements.forEach((element) => {
      if (element.type === "textBox") {
        initialData[element.uniqueId] = element.content;
      } else if (element.type === "multipleChoice") {
        initialData[element.uniqueId] = [];
      } else if (element.type === "date") {
        // For date fields, always use current date instead of stored default value
        initialData[element.uniqueId] = Timestamp.now();
      } else if (element.defaultValue !== undefined) {
        initialData[element.uniqueId] = element.defaultValue;
      } else if (element.type === "checkbox") {
        initialData[element.uniqueId] = false;
      }
    });
  
    setFormData(initialData);
  
    // Ensure we don't display the form until it's initialized
    setTimeout(() => setLoading(false), 200); // Small delay to smooth rendering
  
    if (onHeightChange && !isModal) {
      setTimeout(onHeightChange, 100);
    }
  }, []);

  // Re-adjust height whenever form data changes (for embedded)
  useEffect(() => {
    if (onHeightChange && !isModal) {
      setTimeout(onHeightChange, 100);
    }
  }, [formData, onHeightChange, isModal]);

  const handleInputChange = (uniqueId, value, element) => {
    setFormData((prev) => {
      const updated = { ...prev };
      switch (element.valueType) {
        case "timestamp":
          updated[uniqueId] = value
            ? Timestamp.fromDate(new Date(value))
            : null;
          break;
        case "boolean":
          if (element.type === "yesNo") {
            updated[uniqueId] = value === "Yes";
          } else {
            updated[uniqueId] = Boolean(value);
          }
          break;
        case "image":
          updated[uniqueId] = value;
          break;
        default:
          updated[uniqueId] = value;
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Find the submit button and disable it to prevent multiple clicks
    const submitButton = document.getElementById("form-submit-button");
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.innerHTML = "Submitting...";
    }

    try {
      // 1) Copy signature data
      const newFormData = { ...formData };
      form.elements.forEach((element) => {
        // Keep the existing signature handling code
        if (element.type === "signature") {
          const sigPadRef = signaturePadRefs.current[element.uniqueId]?.current;
          if (sigPadRef && !sigPadRef.isEmpty()) {
            try {
              // Get the original signature data
              const signatureData = sigPadRef
                .getTrimmedCanvas()
                .toDataURL("image/png");

              // Save the original signature data - no processing
              newFormData[element.uniqueId] = signatureData;

              // Log to verify we have data
              console.log(`Signature saved for ${element.uniqueId}`);
            } catch (error) {
              console.error("Error saving signature:", error);
              newFormData[element.uniqueId] = "";
            }
          } else {
            newFormData[element.uniqueId] = "";
          }
        }

        // Process header images with proper defaults
        if (element.type === "headerImage" && !newFormData[element.uniqueId]) {
          newFormData[element.uniqueId] = {
            imageUrl: element.imageUrl,
            fileName: element.fileName,
            size: element.size || "medium",
            alignment: element.alignment || "center",
            customHeight: element.customHeight || null, // Fix for undefined error
          };
        }

        // Handle textBox content
        if (element.type === "textBox" && !newFormData[element.uniqueId]) {
          newFormData[element.uniqueId] = element.content || "";
        }
      });

      // 2) Validate required fields - keep existing validation code
      for (const element of form.elements) {
        if (!element.required) continue;

        const value = newFormData[element.uniqueId];
        if (element.type === "signature") {
          if (!value) {
            alert(`Please fill in the required field: ${element.label}`);
            // Re-enable the button if validation fails
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.innerHTML = "Submit Form";
            }
            return;
          }
        } else if (element.type === "multipleChoice") {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            alert(`Please fill in the required field: ${element.label}`);
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.innerHTML = "Submit Form";
            }
            return;
          }
        } else if (element.type === "checkbox") {
          if (!value) {
            alert(`Please fill in the required field: ${element.label}`);
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.innerHTML = "Submit Form";
            }
            return;
          }
        } else {
          if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && !value.trim())
          ) {
            alert(`Please fill in the required field: ${element.label}`);
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.innerHTML = "Submit Form";
            }
            return;
          }
        }
      }

      // 3) Build final object
      const formDataWithSignatures = { ...newFormData };

      // 4) Build element metadata
      const elementMetadata = {};
      form.elements.forEach((element) => {
        elementMetadata[element.uniqueId] = {
          label: element.label,
          type: element.type,
          options: element.options,
          valueType: element.valueType,
          mapToIntake: element.mapToIntake || null,
        };
      });

      // 5) Final submission data
      const submissionData = {
        formId: form.id,
        formName: form.name,
        dateSubmitted: Timestamp.now(),
        residentId: form.residentId || "",
        residentFullName: form.residentFullName || "",
        responses: formDataWithSignatures,
        elementMetadata,
        elementOrder: form.elementOrder,
        submittedBy: user
          ? {
              userId: user.uid,
              email: user.email,
              displayName: user.displayName,
            }
          : { anonymous: true },
        // Add a flag to indicate this is the first submission attempt
        initialSubmission: true,
      };

      // Add event context if this form is from an event
      if (form.eventId) {
        submissionData.eventId = form.eventId;
        submissionData.eventTitle = form.eventTitle || "";
        submissionData.eventDate = form.eventDate;
      }

      // Add recurring form context if applicable
      if (form.isReoccurringForm) {
        submissionData.isReoccurringForm = true;
        submissionData.reoccurrenceType = form.reoccurrenceType || "weekly";
        submissionData.formFrequency = form.reoccurrenceType || "weekly";
      }

      // If the form has frequency specified (weekly, monthly, etc.)
      if (form.formFrequency) {
        submissionData.formFrequency = form.formFrequency;
      }

      console.log("Submission Data:", submissionData);

      // 6) Attempt submit
      try {
        await onSubmit(submissionData);
        // If embedded => show success inline
        if (!isModal) {
          setSubmitted(true);
          if (onHeightChange) setTimeout(onHeightChange, 100);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        alert("Error submitting form. Please try again. " + error.message);
        // Re-enable the button on error
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.innerHTML = "Submit Form";
        }
      }
    } catch (error) {
      console.error("Error preparing form data:", error);
      alert("Error preparing form data. Please try again.");
      // Re-enable the button on error
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.innerHTML = "Submit Form";
      }
    }
  };

  // If non-modal and user already submitted => success message
  if (!isModal && submitted) {
    return (
      <div ref={containerRef} className="bg-white p-4 rounded-lg">
        <div className="bg-green-500/20 p-4 rounded-lg text-center">
          <h3 className="text-gray-800 text-lg font-semibold">
            Form Submitted Successfully!
          </h3>
          <p className="text-gray-600 mt-2">Thank you for your submission.</p>
        </div>
      </div>
    );
  }

  const renderFormElement = (element) => {
    const uniqueId = element.uniqueId;
    switch (element.type) {
      case "headerImage":
        let imageHeight = "12rem"; // Default/medium
        if (element.size === "small") imageHeight = "8rem";
        if (element.size === "large") imageHeight = "16rem";
        if (element.size === "custom")
          imageHeight = `${element.customHeight || 150}px`;

        let alignmentClass = "mx-auto"; // center default
        if (element.alignment === "left") alignmentClass = "ml-0 mr-auto";
        if (element.alignment === "right") alignmentClass = "mr-0 ml-auto";

        return (
          <div className="mb-6">
            {element.imageUrl && (
              <div className="relative">
                <img
                  src={element.imageUrl}
                  alt="Header Image"
                  className={`max-w-full ${alignmentClass} rounded-lg object-contain`}
                  style={{ maxHeight: imageHeight }}
                />
              </div>
            )}
          </div>
        );
      // Add this case inside the switch statement in renderFormElement
      case "applicantName":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  className={`w-full px-4 py-2 ${
                    isModal
                      ? "bg-slate-800/50 border-slate-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } border rounded-lg focus:outline-none focus:border-blue-500`}
                  value={formData[uniqueId]?.split(" ")[0] || ""}
                  onChange={(e) => {
                    const firstName = e.target.value;
                    const lastName =
                      formData[uniqueId]?.split(" ").slice(1).join(" ") || "";
                    const fullName = `${firstName} ${lastName}`.trim();
                    handleInputChange(uniqueId, fullName, element);
                  }}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Last Name"
                  className={`w-full px-4 py-2 ${
                    isModal
                      ? "bg-slate-800/50 border-slate-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } border rounded-lg focus:outline-none focus:border-blue-500`}
                  value={
                    formData[uniqueId]?.split(" ").slice(1).join(" ") || ""
                  }
                  onChange={(e) => {
                    const lastName = e.target.value;
                    const firstName = formData[uniqueId]?.split(" ")[0] || "";
                    const fullName = `${firstName} ${lastName}`.trim();
                    handleInputChange(uniqueId, fullName, element);
                  }}
                />
              </div>
            </div>
          </div>
        );
      case "input":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              className={`w-full px-4 py-2 ${
                isModal
                  ? "bg-slate-800/50 border-slate-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              } border rounded-lg focus:outline-none focus:border-blue-500`}
              value={formData[uniqueId] || ""}
              onChange={(e) =>
                handleInputChange(uniqueId, e.target.value, element)
              }
            />
          </div>
        );
      case "textBox":
        // Determine text styles based on formatting options
        const textStyles = {
          fontWeight: element.isBold ? "bold" : "normal",
          fontStyle: element.isItalic ? "italic" : "normal",
          textDecoration: element.isUnderline ? "underline" : "none",
          textAlign: element.textAlign || "left",
        };

        // Determine font size
        let fontSize = "1rem"; // medium (default)
        if (element.fontSize === "small") fontSize = "0.875rem";
        if (element.fontSize === "large") fontSize = "1.25rem";

        return (
          <div className="mb-4">
            <div
              className={`${
                isModal ? "text-white" : "text-gray-900"
              } whitespace-pre-wrap`}
              style={{
                ...textStyles,
                fontSize,
              }}
            >
              {element.content}
            </div>
          </div>
        );
      case "dropdown":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <select
              className={`w-full px-4 py-2 ${
                isModal
                  ? "bg-slate-800/50 border-slate-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              } border rounded-lg focus:outline-none focus:border-blue-500`}
              value={formData[uniqueId] || ""}
              onChange={(e) =>
                handleInputChange(uniqueId, e.target.value, element)
              }
            >
              <option value="">Select an option</option>
              {element.options?.map((option, i) => (
                <option key={i} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      case "phone":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="tel"
              className={`w-full px-4 py-2 ${
                isModal
                  ? "bg-slate-800/50 border-slate-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              } border rounded-lg focus:outline-none focus:border-blue-500`}
              placeholder="XXX-XXX-XXXX"
              value={formData[uniqueId] || ""}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                handleInputChange(uniqueId, formatted, element);
              }}
              maxLength={12}
            />
          </div>
        );
      case "yesNo":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <select
              className={`w-full px-4 py-2 ${
                isModal
                  ? "bg-slate-800/50 border-slate-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              } border rounded-lg focus:outline-none focus:border-blue-500`}
              value={
                formData[uniqueId] === true
                  ? "Yes"
                  : formData[uniqueId] === false
                  ? "No"
                  : ""
              }
              onChange={(e) =>
                handleInputChange(uniqueId, e.target.value, element)
              }
            >
              <option value="">Select an option</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        );
      case "date":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              className={`w-full px-4 py-2 ${
                isModal
                  ? "bg-slate-800/50 border-slate-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              } border rounded-lg focus:outline-none focus:border-blue-500`}
              value={
                formData[uniqueId] instanceof Timestamp
                  ? new Date(formData[uniqueId].seconds * 1000)
                      .toISOString()
                      .split("T")[0]
                  : formData[uniqueId] || ""
              }
              onChange={(e) =>
                handleInputChange(uniqueId, e.target.value, element)
              }
            />
          </div>
        );
      case "multipleChoice":
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
              {element.options?.map((option, i) => (
                <label
                  key={i}
                  className={`flex items-center gap-2 ${
                    isModal ? "text-white" : "text-gray-900"
                  }`}
                >
                  <input
                    type="checkbox"
                    value={option}
                    checked={
                      Array.isArray(formData[uniqueId]) &&
                      formData[uniqueId].includes(option)
                    }
                    onChange={(e) => {
                      const currentSelections = Array.isArray(
                        formData[uniqueId]
                      )
                        ? [...formData[uniqueId]]
                        : [];
                      if (e.target.checked) {
                        handleInputChange(
                          uniqueId,
                          [...currentSelections, option],
                          element
                        );
                      } else {
                        handleInputChange(
                          uniqueId,
                          currentSelections.filter((item) => item !== option),
                          element
                        );
                      }
                    }}
                    className="rounded text-blue-500 focus:ring-blue-500"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div className="mb-4">
            <label
              className={`flex items-center gap-2 ${
                isModal ? "text-white" : "text-gray-900"
              }`}
            >
              <input
                type="checkbox"
                checked={formData[uniqueId] || false}
                onChange={(e) =>
                  handleInputChange(uniqueId, e.target.checked, element)
                }
                className="rounded text-blue-500 focus:ring-blue-500"
              />
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
          </div>
        );
      case "signature":
        // Ensure a persistent ref is created for this signature pad.
        if (!signaturePadRefs.current[uniqueId]) {
          signaturePadRefs.current[uniqueId] = React.createRef();
        }
        return (
          <div className="mb-4">
            <label
              className={`block text-sm font-medium ${
                isModal ? "text-slate-300" : "text-gray-700"
              } mb-1`}
            >
              {element.label}{" "}
              {element.required && <span className="text-red-500">*</span>}
            </label>
            <div
              className={`w-full ${
                isModal
                  ? "bg-slate-800/50 border-slate-700"
                  : "bg-gray-50 border-gray-300"
              } border rounded-lg overflow-hidden`}
            >
              <SignaturePad
                canvasProps={{
                  width: 500, // more horizontal pixels
                  height: 200, // more vertical pixels
                  style: {
                    // Optionally override or match your Tailwind classes:
                    width: "100%",
                    height: "200px",
                    backgroundColor: isModal ? "#0F172A" : "#FFFFFF", //
                  },
                }}
                // Make pen a bit thinner since we now have more real estate
                dotSize={1.5}
                minWidth={1.5}
                maxWidth={2}
                penColor={isModal ? "white" : "black"}
                ref={signaturePadRefs.current[uniqueId]}
              />
            </div>
            <button
              onClick={() =>
                signaturePadRefs.current[uniqueId].current?.clear()
              }
              className={`mt-2 px-3 py-1 text-sm ${
                isModal
                  ? "bg-slate-800 hover:bg-slate-700 text-slate-300"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } rounded-md transition-colors`}
            >
              Clear
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Render success message for non-modal forms after submission
  if (!isModal && submitted) {
    return (
      <div ref={containerRef} className="bg-slate-900 p-4 rounded-lg">
        <div className="bg-green-500/20 p-4 rounded-lg text-center">
          <h3 className="text-white text-lg font-semibold">
            Form Submitted Successfully!
          </h3>
          <p className="text-white/80 mt-2">Thank you for your submission.</p>
        </div>
      </div>
    );
  }

  // Content is the same for both modal and non-modal, just wrapped differently
  const formContent = (
    <>
      {/* Form Title + (optional) close button */}
      <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
        <h3
          className={`text-xl font-semibold w-full text-center ${
            isModal ? "text-white" : "text-gray-800"
          }`}
        >
          {form.name}
        </h3>

        {isModal && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="text-slate-400 hover:text-slate-300" size={20} />
          </button>
        )}
      </div>

      {/* 
        Main content:
        For a tall form, we let it scroll internally up to 100vh.
        You can remove "max-h-[100vh]" if you prefer no internal limit.
      */}
      <div className="p-6 max-h-[100vh] overflow-y-auto">
        {form.elements.map((element) => (
          <div key={element.uniqueId}>{renderFormElement(element)}</div>
        ))}
      </div>

      {/* Form footer: Cancel + Submit */}
      <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
        {isModal && (
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          id="form-submit-button"
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Submit Form
        </button>
      </div>
    </>
  );

  // If isModal => show the "overlay" style (direct URL usage)
  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Dark semi-transparent overlay with blur effect */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <div className="relative w-full h-full flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl h-[90vh] overflow-y-auto">
            {formContent}
          </div>
        </div>
      </div>
    );
  }

  // If not isModal => embedded usage => no big overlay
  // but you can add or remove padding here if you do want some margin
  return (
    <div
      ref={containerRef}
      className="bg-white border border-gray-200 rounded-xl shadow-lg w-full"
    >
      {formContent}
    </div>
  );
};

export default FormRenderer;
